import { debounce } from 'lodash-es'
import { useLocale, useTranslations } from 'next-intl'

import { MAX_USING_FEE, MIN_USING_FEE } from '@/entities/map'
import { cn } from '@/shared/lib'
import { Slider } from '@/shared/ui'

import {
  ENGLISH_USING_FEE_VALUE_FORMAT,
  USING_FEE_VALUE_FORMAT,
} from './value-format'
import { Description, Title, useFilterLayout } from '../../common/layout'

type Props = {
  ref: (element: HTMLDivElement) => void
  value: number[]
  onValueChange: (value: number[]) => void
  className?: string
}

export const UsingFeeInDialogView = ({
  ref,
  value,
  onValueChange,
  className,
}: Props) => {
  const locale = useLocale()
  const isEnglish = locale === 'en'

  const t = useTranslations('filter')
  const layout = useFilterLayout()
  const isHorizontal = layout === 'horizontal'
  // PC(vertical)는 Track → RangeInput, 모바일/태블릿은 RangeInput → Track
  const isReordered = layout !== 'vertical'

  const rangeInput = (
    <Slider.RangeInput
      className={isHorizontal ? 'w-full' : undefined}
      inputClassName={cn(
        isHorizontal && 'grow',
        isEnglish && 'gap-0 *:text-start',
      )}
    />
  )

  const slider = (
    <Slider.Root
      value={value}
      onValueChange={debounce(onValueChange, 500)}
      min={MIN_USING_FEE}
      max={MAX_USING_FEE}
      step={50000}
      minStepsBetweenThumbs={1}
      valueFormat={
        isEnglish ? ENGLISH_USING_FEE_VALUE_FORMAT : USING_FEE_VALUE_FORMAT
      }
    >
      {isReordered ? (
        <>
          {rangeInput}
          <Slider.Track />
        </>
      ) : (
        <>
          <Slider.Track />
          {rangeInput}
        </>
      )}
    </Slider.Root>
  )

  if (isHorizontal) {
    return (
      <div className={className} ref={ref}>
        <div className="flex w-[200px] shrink-0 items-center gap-3">
          <Title className="w-fit">{t('price.title')}</Title>
          <Description>{t('price_subtitle')}</Description>
        </div>
        <div className="min-w-0 flex-1">{slider}</div>
      </div>
    )
  }

  return (
    <div className={className} ref={ref}>
      <div className={cn('mb-3 flex items-center justify-between')}>
        <div className="flex items-center gap-3">
          <Title className="mb-0">{t('price.title')}</Title>
          <Description>{t('price_subtitle')}</Description>
        </div>
      </div>
      {slider}
    </div>
  )
}
